import React, { FC, KeyboardEvent, useState } from 'react';
import styled, { keyframes } from 'styled-components';

import { Icon } from '@column/column-ui-kit';
import { TextOverflow, type TextOverflowAlign } from '@column/ui';

import { Text } from '~/styles';
import { copyToClipboard } from '~/util';

const PillContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const PillValue = styled.span`
  font-size: 13px;
`;

const pop = keyframes`
  0% {
    transform: scale(0.4);
    opacity: 0;
  }
  60% {
    transform: scale(1.12);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const CopyGlyph = styled(Icon.Copy)`
  --icon-size: 14px;
  --icon-color: rgb(255 255 255 / 0.7);
`;

const CheckGlyph = styled(Icon.Checkmark)`
  --icon-size: 14px;
  --icon-color: ${({ theme }) => theme.success.background};
  animation: ${pop} 0.2s ease-out;
`;

interface CopyableValueProps {
  value?: string;
  /** Anchor edge for the reveal popover. Use "right" for values near the viewport edge. */
  align?: TextOverflowAlign;
  /** When false, render the full value instead of masking to the last four characters. */
  mask?: boolean;
  /** Render the visible value in a monospace, regular-weight style (e.g. crypto addresses). */
  monospace?: boolean;
}

// A copyable value that reveals the full value in a popover on hover (via TextOverflow) and copies
// it on click. By default it masks to the last four characters behind four dots; pass mask={false}
// to show the full value.
export const CopyableValue: FC<CopyableValueProps> = ({ value, align = 'left', mask = true, monospace = false }) => {
  const [copied, setCopied] = useState(false);

  if (!value) {
    return (
      <Text as="span" size={16} weight="medium" color="foreground">
        -
      </Text>
    );
  }

  const handleCopy = () => {
    copyToClipboard(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleCopy();
    }
  };

  const popover = (
    <PillContent>
      <PillValue className={monospace ? 'font-mono' : undefined}>{value}</PillValue>
      {copied ? <CheckGlyph key="check" /> : <CopyGlyph key="copy" />}
    </PillContent>
  );

  return (
    <TextOverflow
      align={align}
      delay={0}
      alwaysShow
      nowrap
      hoverContent={popover}
      pillClassName="rounded-[8px] bg-gray-900/85 text-white ring-0 backdrop-blur-md shadow-[0_4px_12px_-2px_rgb(0_0_0/0.4),inset_0_0_0_1px_rgb(0_0_0/0.6)] dark:shadow-[0_4px_12px_-2px_rgb(0_0_0/0.4),inset_0_0_0_1px_rgb(0_0_0/0.6)]"
      className="w-full cursor-pointer text-base font-medium leading-5"
      role="button"
      tabIndex={0}
      aria-label={`Copy ${value}`}
      onClick={handleCopy}
      onKeyDown={handleKeyDown}
    >
      {monospace ? (
        <span className="font-mono text-sm font-normal text-foreground">{mask ? `••••${value.slice(-4)}` : value}</span>
      ) : (
        <Text as="span" size={16} weight="medium" color="foreground">
          {mask ? `••••${value.slice(-4)}` : value}
        </Text>
      )}
    </TextOverflow>
  );
};
